import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCustomizationStore } from '../store/customizationStore';
import { useLocalizationStore } from '../store/localizationStore';
import { formatCurrency } from '../utils/units';
import { MaterialDialog } from './MaterialDialog';
import { materials } from '../data/materials';
import { benefits } from '../data/customization';
import { 
  Star, 
  Award, 
  Shield, 
  Clock, 
  MessageCircle, 
  X, 
  Quote, 
  MapPin, 
  ChevronLeft, 
  ChevronRight,
  Sparkles,
  Truck,
  Wrench,
  Heart,
  ArrowRight,
  Plus,
  Minus,
  Calculator,
  Info,
  Square,
  Phone,
  Mail,
  Calendar,
  Gem,
  Zap,
  AlertCircle,
  Ruler,
  Palette,
  Users,
  Timer,
  BadgeCheck,
  SunMoon
} from 'lucide-react';

const VAT_RATE = 0.21; // 21% VAT

const containerVariants = {
  hidden: { opacity: 0, x: '100%' },
  visible: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: '-100%' }
};

const cardVariants = {
  initial: { 
    scale: 1,
    borderColor: 'rgba(180, 160, 108, 0.2)',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
  },
  hover: { 
    scale: 1.02,
    borderColor: 'rgba(180, 160, 108, 1)',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    transition: {
      duration: 0.2,
      ease: 'easeOut'
    }
  },
  tap: { 
    scale: 0.98,
    borderColor: 'rgba(139, 115, 85, 1)',
    transition: {
      duration: 0.1,
      ease: 'easeOut'
    }
  }
};

const glowVariants = {
  initial: {
    opacity: 0,
    scale: 0.95
  },
  hover: {
    opacity: [0.4, 0.2, 0.4],
    scale: 1.05,
    transition: {
      duration: 1.5,
      repeat: Infinity,
      repeatType: "reverse",
      ease: "linear"
    }
  }
};

export const PurchasePath: React.FC = () => {
  const { setPath, setStep, selectedMaterial, setSelectedMaterials: setStoreSelectedMaterials } = useCustomizationStore();
  const { currency, language } = useLocalizationStore();
  const [showSlabSelection, setShowSlabSelection] = useState(false);
  const [showMaterialDialog, setShowMaterialDialog] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [selectedThickness, setSelectedThickness] = useState<number>(12);
  const [localSelectedMaterials, setLocalSelectedMaterials] = useState(
    selectedMaterial ? [{ material: selectedMaterial, quantity: 1, area: 5.12 }] : []
  );

  const handlePathSelect = (path: 'slab-only' | 'custom') => {
    if (path === 'slab-only') {
      setShowSlabSelection(true);
    } else {
      setPath(path);
      setStep('customization-type');
    }
  };

  const handleQuantityChange = (materialId: string, delta: number) => {
    setLocalSelectedMaterials(prev => prev.map(item => {
      if (item.material.id === materialId) {
        const newQuantity = Math.max(1, item.quantity + delta);
        return {
          ...item,
          quantity: newQuantity,
          area: newQuantity * 5.12
        };
      }
      return item;
    }));
  };

  const handleAreaChange = (materialId: string, newArea: number) => {
    if (newArea > 0) {
      const slabCount = Math.ceil(newArea / 5.12);
      setLocalSelectedMaterials(prev => prev.map(item => {
        if (item.material.id === materialId) {
          return {
            ...item,
            quantity: slabCount,
            area: newArea
          };
        }
        return item;
      }));
    }
  };

  const handleAddMaterial = (material: Material) => {
    if (!localSelectedMaterials.some(item => item.material.id === material.id)) {
      setLocalSelectedMaterials(prev => [...prev, { 
        material, 
        quantity: 1,
        area: 5.12
      }]);
    }
    setShowMaterialDialog(false);
  };

  const handleRemoveMaterial = (materialId: string) => {
    setLocalSelectedMaterials(prev => prev.filter(item => item.material.id !== materialId));
  };

  const handleFinishChange = (materialId: string, finish: 'glossy' | 'matte') => {
    setLocalSelectedMaterials(prev => prev.map(item => 
      item.material.id === materialId 
        ? { ...item, material: { ...item.material, selectedFinish: finish } }
        : item
    ));
  };

  const handleThicknessSelect = (thickness: number) => {
    if (thickness === 20) {
      setShowRequestModal(true);
      return;
    }
    setSelectedThickness(thickness);
  };

  const handleSlabPurchase = () => {
    setPath('slab-only');
    setStoreSelectedMaterials(localSelectedMaterials.map(item => ({
      ...item,
      material: {
        ...item.material,
        thickness: selectedThickness
      }
    })));
    setStep('inquiry');
  };

  const calculateSubtotal = () => {
    return localSelectedMaterials.reduce((total, item) => {
      return total + (item.material.price * item.area);
    }, 0);
  };

  const calculateTotalPrice = () => {
    const subtotal = calculateSubtotal();
    const vat = subtotal * VAT_RATE;
    return subtotal + vat;
  };

  if (showSlabSelection) {
    return (
      <motion.div
        initial={{ opacity: 0, x: '100%' }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: '-100%' }}
        className="min-h-screen bg-cream py-16"
      >
        <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => setShowSlabSelection(false)}
            className="flex items-center text-black/70 hover:text-black mb-8"
          >
            <ChevronLeft className="w-5 h-5 mr-2" />
            Back to Options
          </button>

          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gold mb-4">
              Select Your Slabs
            </h2>
            <p className="text-xl text-black/70">
              Choose your materials and specify the quantity
            </p>
          </div>

          {/* Thickness Selection */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <h3 className="text-lg font-semibold text-gold-dark mb-4">Thickness Selection</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[12, 20].map((thickness) => {
                const isSelected = selectedThickness === thickness;
                const isUponRequest = thickness === 20;

                return (
                  <motion.button
                    key={thickness}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleThicknessSelect(thickness)}
                    className={`p-4 rounded-xl relative ${
                      isSelected 
                        ? 'bg-gold text-white'
                        : 'bg-white text-gold-dark hover:bg-gold/5'
                    }`}
                  >
                    <span className="block text-lg font-semibold">
                      {thickness}mm
                    </span>
                    <span className="text-sm opacity-80">
                      {thickness === 12 ? 'Standard' : 'Premium'}
                    </span>
                    {isUponRequest && (
                      <span className="absolute top-2 right-2 bg-gold text-white text-xs px-2 py-1 rounded-full">
                        Upon Request
                      </span>
                    )}
                  </motion.button>
                );
              })}
            </div>
          </div>

          {/* Selected Materials */}
          <div className="space-y-6 mb-8">
            {localSelectedMaterials.map((item) => (
              <div 
                key={item.material.id} 
                className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex flex-col sm:flex-row items-start gap-6">
                    <img 
                      src={item.material.image} 
                      alt={item.material.name}
                      className="w-full sm:w-24 h-24 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h4 className="text-lg font-semibold text-gold">{item.material.name}</h4>
                          <p className="text-sm text-black/70">{item.material.description}</p>
                        </div>
                        <button 
                          onClick={() => handleRemoveMaterial(item.material.id)}
                          className="text-black/40 hover:text-black/70"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>

                      {/* Surface Finish Toggle */}
                      <div className="mb-4">
                        <label className="text-sm font-medium text-black/70 mb-2 block">
                          Surface Finish
                        </label>
                        <div className="bg-cream rounded-lg p-4">
                          <div className="flex gap-2">
                            {['glossy', 'matte'].map((finish) => (
                              <button
                                key={finish}
                                onClick={() => handleFinishChange(item.material.id, finish as 'glossy' | 'matte')}
                                className={`flex-1 px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors ${
                                  item.material.selectedFinish === finish
                                    ? 'bg-gold text-white'
                                    : 'bg-white text-gold hover:bg-gold/5'
                                }`}
                              >
                                <SunMoon className="w-4 h-4" />
                                <span className="capitalize">{finish}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* Quantity Controls */}
                        <div className="bg-cream rounded-lg p-4">
                          <label className="text-sm font-medium text-black/70 mb-2 block">
                            Number of Slabs
                          </label>
                          <div className="flex items-center gap-4">
                            <button
                              onClick={() => handleQuantityChange(item.material.id, -1)}
                              className="w-8 h-8 flex items-center justify-center rounded-full bg-white text-black/70 hover:text-black border border-gold/20"
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <span className="text-xl font-semibold text-gold min-w-[2ch] text-center">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => handleQuantityChange(item.material.id, 1)}
                              className="w-8 h-8 flex items-center justify-center rounded-full bg-white text-black/70 hover:text-black border border-gold/20"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Area Input */}
                        <div className="bg-cream rounded-lg p-4">
                          <label className="text-sm font-medium text-black/70 mb-2 block">
                            Total Area Needed
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              value={item.area}
                              onChange={(e) => handleAreaChange(item.material.id, parseFloat(e.target.value))}
                              className="w-full px-3 py-2 border border-gold/20 rounded-lg text-right text-lg font-semibold text-gold bg-white"
                              step="0.01"
                              min="0"
                            />
                            <span className="text-black/70 whitespace-nowrap">m²</span>
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 pt-4 border-t border-gold/20 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <span className="text-sm text-black/70">Price per m²:</span>
                          <span className="ml-2 font-semibold text-gold">
                            {formatCurrency(item.material.price, currency, language)}
                          </span>
                        </div>
                        <div>
                          <span className="text-sm text-black/70">Subtotal:</span>
                          <span className="ml-2 font-semibold text-gold">
                            {formatCurrency(item.material.price * item.area, currency, language)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Add Material Button */}
            <button
              onClick={() => setShowMaterialDialog(true)}
              className="w-full p-6 border-2 border-dashed border-gold/20 rounded-xl text-black/70 hover:border-gold hover:text-gold transition-colors"
            >
              <Plus className="w-6 h-6 mx-auto mb-2" />
              <span className="font-medium">Add Another Material</span>
            </button>
          </div>

          {/* Order Summary */}
          <div className="bg-gradient-to-r from-gold to-gold-dark rounded-xl shadow-lg p-6 sm:p-8 text-white">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6">
              <div>
                <span className="text-white/80">Total Area:</span>
                <p className="text-2xl font-bold">
                  {localSelectedMaterials.reduce((total, item) => total + item.area, 0).toFixed(2)} m²
                </p>
              </div>
              <div>
                <span className="text-white/80">Total Slabs:</span>
                <p className="text-2xl font-bold">
                  {localSelectedMaterials.reduce((total, item) => total + item.quantity, 0)}
                </p>
              </div>
              <div>
                <span className="text-white/80">Selected Thickness:</span>
                <p className="text-lg font-semibold">{selectedThickness}mm</p>
              </div>
              <div>
                <span className="text-white/80">Total Weight:</span>
                <p className="text-lg font-semibold">
                  {(localSelectedMaterials.reduce((total, item) => {
                    // Weight per m² based on thickness
                    const weightPerM2 = selectedThickness === 20 ? 46.875 : 28.125; // kg/m²
                    return total + (item.area * weightPerM2);
                  }, 0)).toFixed(1)} kg
                </p>
              </div>
              <div>
                <span className="text-white/80">Price (excl. VAT):</span>
                <p className="text-2xl font-bold">
                  {formatCurrency(calculateSubtotal(), currency, language)}
                </p>
              </div>
              <div>
                <span className="text-white/80">VAT (21%):</span>
                <p className="text-2xl font-bold">
                  {formatCurrency(calculateSubtotal() * VAT_RATE, currency, language)}
                </p>
              </div>
              <div className="col-span-1 sm:col-span-2">
                <span className="text-white/80">Total Price (incl. VAT):</span>
                <p className="text-2xl font-bold">
                  {formatCurrency(calculateTotalPrice(), currency, language)}
                </p>
              </div>
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSlabPurchase}
              disabled={localSelectedMaterials.length === 0}
              className="w-full bg-white text-gold py-4 rounded-lg font-semibold text-lg hover:bg-cream disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Continue to Quote
            </motion.button>
          </div>

          {/* Benefits */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
            {benefits.map(({ icon: Icon, title, description }) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-center p-4"
              >
                <div className="inline-block p-3 bg-gold/10 rounded-full mb-3">
                  <Icon className="w-6 h-6 text-gold" />
                </div>
                <h3 className="font-semibold text-gold text-sm">{title}</h3>
                <p className="text-xs text-black/70">{description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <MaterialDialog
          show={showMaterialDialog}
          onClose={() => setShowMaterialDialog(false)}
          onSelect={handleAddMaterial}
          materials={materials}
          selectedMaterial={selectedMaterial}
          selectedMaterials={new Set(localSelectedMaterials.map(item => item.material.id))}
          currency={currency}
          language={language}
        />

        {/* Request Modal */}
        <AnimatePresence>
          {showRequestModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
              onClick={() => setShowRequestModal(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-xl p-6 max-w-md mx-4 relative"
                onClick={e => e.stopPropagation()}
              >
                <button
                  onClick={() => setShowRequestModal(false)}
                  className="absolute top-4 right-4 text-black/40 hover:text-black/60"
                >
                  <X className="w-5 h-5" />
                </button>
                
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-gold/10 rounded-lg">
                    <Info className="w-6 h-6 text-gold" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    20mm Thickness Available Upon Request
                  </h3>
                </div>
                
                <p className="text-gray-600 mb-6">
                  The 20mm thickness option requires special ordering. Our team will be happy to assist you with availability and lead times.
                </p>

                <div className="space-y-4 mb-6">
                  <a
                    href="tel:+18001234567"
                    className="flex items-center gap-3 p-3 bg-gold/5 rounded-lg hover:bg-gold/10 transition-colors"
                  >
                    <Phone className="w-5 h-5 text-gold" />
                    <div>
                      <p className="font-medium text-gold-dark">Call Us</p>
                      <p className="text-sm text-black/60">+1 (800) 123-4567</p>
                    </div>
                  </a>

                  <a
                    href="mailto:sales@example.com"
                    className="flex items-center gap-3 p-3 bg-gold/5 rounded-lg hover:bg-gold/10 transition-colors"
                  >
                    <Mail className="w-5 h-5 text-gold" />
                    <div>
                      <p className="font-medium text-gold-dark">Email Us</p>
                      <p className="text-sm text-black/60">sales@example.com</p>
                    </div>
                  </a>
                </div>

                <div className="flex flex-col gap-3">
                  <button
                    onClick={() => {
                      setSelectedThickness(20);
                      setShowRequestModal(false);
                    }}
                    className="w-full px-4 py-3 bg-gold text-white rounded-lg hover:bg-gold-dark font-medium"
                  >
                    Still select 20mm
                  </button>

                  <button
                    onClick={() => {
                      setSelectedThickness(12);
                      setShowRequestModal(false);
                    }}
                    className="w-full px-4 py-3 bg-white text-gold border-2 border-gold rounded-lg hover:bg-gold/5 font-medium"
                  >
                    Select 12mm Instead
                  </button>

                  <button
                    onClick={() => setShowRequestModal(false)}
                    className="w-full px-4 py-3 text-gray-600 hover:text-gray-800"
                  >
                    Close
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: '100%' }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: '-100%' }}
      className="min-h-screen bg-cream py-16"
    >
      <div className="w-full max-w-6xl mx-auto px-8">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-5xl font-bold text-gold mb-6">
              Choose Your Path
            </h2>
            <p className="text-xl text-black/70">
              Select how you'd like to proceed with your project
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <motion.div
            variants={cardVariants}
            initial="initial"
            whileHover="hover"
            whileTap="tap"
            className="relative bg-white/80 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden cursor-pointer border-2 border-gold/20"
            onClick={() => handlePathSelect('slab-only')}
          >
            {/* Glow Effect */}
            <motion.div
              variants={glowVariants}
              className="absolute -inset-2 bg-gradient-to-r from-gold/20 to-gold-dark/20 rounded-xl blur-xl z-0"
            />
            
            <div className="relative z-10 p-8">
              <Ruler className="w-16 h-16 text-gold mb-6" />
              <h3 className="text-2xl font-semibold text-gold mb-4">Buy Slab Only</h3>
              <div className="space-y-3 mb-6">
                <p className="text-black/70">
                  Perfect for contractors and DIY enthusiasts. Purchase premium quality material and work with your preferred fabricator.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center text-sm text-black/70">
                    <ArrowRight className="w-4 h-4 text-gold mr-2" />
                    Wholesale pricing available
                  </li>
                  <li className="flex items-center text-sm text-black/70">
                    <ArrowRight className="w-4 h-4 text-gold mr-2" />
                    Flexible pickup options
                  </li>
                  <li className="flex items-center text-sm text-black/70">
                    <ArrowRight className="w-4 h-4 text-gold mr-2" />
                    Expert material consultation
                  </li>
                </ul>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-black/60">
                  Starting from {formatCurrency(selectedMaterial?.price || 0, currency, language)}/m²
                </span>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-gold text-white px-4 py-2 rounded-lg font-medium hover:bg-gold-dark transition-colors"
                >
                  Select Option
                </motion.button>
              </div>
            </div>
            <div className="px-8 py-4 bg-gold/10 border-t border-gold/20">
              <p className="text-sm text-gold font-medium">
                🚚 Free delivery for orders over 40 m²
              </p>
            </div>
          </motion.div>

          <motion.div
            variants={cardVariants}
            initial="initial"
            whileHover="hover"
            whileTap="tap"
            className="relative bg-white/80 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden cursor-pointer border-2 border-gold/20"
            onClick={() => handlePathSelect('custom')}
          >
            {/* Glow Effect */}
            <motion.div
              variants={glowVariants}
              className="absolute -inset-2 bg-gradient-to-r from-gold/20 to-gold-dark/20 rounded-xl blur-xl z-0"
            />
            
            <div className="absolute top-4 right-4 z-10 bg-gold text-white px-3 py-1 rounded-full">
              <span className="text-sm font-medium">Most Popular</span>
            </div>
            
            <div className="relative z-10 p-8">
              <Palette className="w-16 h-16 text-gold mb-6" />
              <h3 className="text-2xl font-semibold text-gold mb-4">Custom Design</h3>
              <div className="space-y-3 mb-6">
                <p className="text-black/70">
                  Let our expert team handle everything. From design to installation, we'll create your perfect custom piece.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center text-sm text-black/70">
                    <ArrowRight className="w-4 h-4 text-gold mr-2" />
                    Professional installation included
                  </li>
                  <li className="flex items-center text-sm text-black/70">
                    <ArrowRight className="w-4 h-4 text-gold mr-2" />
                    10-year warranty
                  </li>
                  <li className="flex items-center text-sm text-black/70">
                    <ArrowRight className="w-4 h-4 text-gold mr-2" />
                    Free design consultation
                  </li>
                </ul>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-black/60">Full service solution</span>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-gold text-white px-4 py-2 rounded-lg font-medium hover:bg-gold-dark transition-colors"
                >
                  Start Design
                </motion.button>
              </div>
            </div>
            <div className="px-8 py-4 bg-gold/10 border-t border-gold/20">
              <p className="text-sm text-gold font-medium">
                ✨ Save 15% on installation this month
              </p>
            </div>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12 w-full">
          {benefits.map(({ icon: Icon, title, description }) => (
            <motion.div
              key={title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-center p-4"
            >
              <div className="inline-block p-3 bg-gold/10 rounded-full mb-3">
                <Icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-semibold text-gold text-sm">{title}</h3>
              <p className="text-xs text-black/70">{description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};